/**
 * Phép tính thuần của biểu đồ kết quả (port phần số học của `components/BieuDo.tsx` native). SVG dựng ở component;
 * ở đây chỉ số — test được bằng `node:test`.
 */

/** Ô → số. Số `numeric` từ SQL Server/Postgres về dạng chuỗi; ô không đọc được là 0. */
export function so(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Ô → nhãn. `null` là chữ của lập trình viên, hiện gạch dài. */
export function chu(v: unknown): string {
  return v === null || v === undefined ? '—' : String(v);
}

export interface DonViGon {
  ty: string;
  trieu: string;
  nghin: string;
}

/** `1234567` → `1,23 tr`. Trục không có chỗ cho chín chữ số. Đơn vị và dấu thập phân đi theo ngôn ngữ. */
export function gonSo(n: number, locale: string, dv: DonViGon): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toLocaleString(locale, { maximumFractionDigits: 2 })} ${dv.ty}`;
  if (a >= 1e6) return `${(n / 1e6).toLocaleString(locale, { maximumFractionDigits: 2 })} ${dv.trieu}`;
  if (a >= 1e4) return `${(n / 1e3).toLocaleString(locale, { maximumFractionDigits: 1 })} ${dv.nghin}`;
  return n.toLocaleString(locale, { maximumFractionDigits: 2 });
}

/**
 * Mốc trục đẹp — 1/2/2,5/5 × 10ⁿ. Dừng khi đã PHỦ `max`, không phải khi gần chạm nó: điều kiện cũ bỏ mốc cuối mỗi khi
 * `max` vượt mốc ấy quá nửa bậc, và cột cao nhất trèo lên trên đường lưới trên cùng rồi bị mép khung cắt ngọn (đo được
 * với doanh thu 4,97 tỷ: mốc cuối 4 tỷ, cột vượt trần 24%).
 */
export function mocTruc(max: number, soMoc = 4): number[] {
  if (max <= 0) return [0];
  const tho = max / soMoc;
  const bac = Math.pow(10, Math.floor(Math.log10(tho)));
  const buoc = [1, 2, 2.5, 5, 10].map((k) => k * bac).find((k) => k >= tho) ?? 10 * bac;
  const ra: number[] = [];
  for (let v = 0; v < max + buoc; v += buoc) ra.push(v);
  return ra;
}

/**
 * Gom dòng thành `x → loạt → số`. Cộng dồn thay vì ghi đè: hai dòng cùng (x, loạt) là chuyện có thật khi câu SQL không
 * gộp hết, và ghi đè sẽ bỏ im lặng một trong hai.
 */
export function gom(rows: readonly Record<string, unknown>[], xk: string, lk: string, yk: string) {
  const xs: string[] = [];
  const ls: string[] = [];
  const o = new Map<string, number>();
  for (const r of rows) {
    const x = chu(r[xk]);
    const l = chu(r[lk]);
    if (!xs.includes(x)) xs.push(x);
    if (!ls.includes(l)) ls.push(l);
    const k = `${x}\u0000${l}`;
    o.set(k, (o.get(k) ?? 0) + so(r[yk]));
  }
  return { xs, ls, lay: (x: string, l: string) => o.get(`${x}\u0000${l}`) ?? 0 };
}

/**
 * Chia giá trị thành ô cho biểu đồ phân phối — quy tắc căn bậc hai, chặn 4..20 (trên 20 mỗi cột chỉ còn một quan sát).
 */
export function phanO(gtTho: readonly number[]): { min: number; buoc: number; dem: number[] } {
  const gt = [...gtTho].sort((a, b) => a - b);
  const soO = Math.max(4, Math.min(20, Math.round(Math.sqrt(gt.length))));
  const min = gt[0] ?? 0;
  const max = gt[gt.length - 1] ?? 1;
  const buoc = (max - min) / soO || 1;
  const dem = Array.from({ length: soO }, () => 0);
  for (const v of gt) {
    const i = Math.min(soO - 1, Math.floor((v - min) / buoc));
    dem[i] = (dem[i] ?? 0) + 1;
  }
  return { min, buoc, dem };
}

/** Bề rộng cột bảng (px) ước từ tiêu đề và 50 dòng đầu; trần 320 px, ô dài hơn cắt `…`. */
export function beRongCot(tieuDe: string, doDaiO: readonly number[]): number {
  const dai = Math.max(tieuDe.length, ...doDaiO.slice(0, 50));
  return Math.round(Math.min(320, Math.max(80, dai * 7.5 + 24)));
}

const CHUOI_SO = /^-?\d+(\.\d+)?$/;

/**
 * Cột toàn số (bỏ qua ô rỗng) → căn phải. Nhận cả chuỗi số nhưng CHỈ để căn lề, không định dạng lại: mã hàng "0012"
 * cũng khớp mẫu, và định dạng nó thành "12" là làm sai dữ liệu.
 */
export function laCotSo(rows: readonly Record<string, unknown>[], ten: string): boolean {
  let coSo = false;
  for (const r of rows.slice(0, 50)) {
    const v = r[ten];
    if (v === null || v === undefined || v === '') continue;
    if (typeof v !== 'number' && !(typeof v === 'string' && CHUOI_SO.test(v))) return false;
    coSo = true;
  }
  return coSo;
}
